import house from "../assets/images/shutterstock_617467400-min.jpg";
import constants from "../constants/constants";
import { useState } from "react";
import SEO, { pageSEO } from "../components/seo.component";
import { motion } from "framer-motion";
import { fadeUp, stagger } from "../functions/animations";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChartLine, faHouse, faKey } from "@fortawesome/free-solid-svg-icons";

export default function Evaluation() {
  const [popup, setPopup] = useState(false);

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    propertyAddress: "",
    city: "",
    provinceState: "",
    propertySize: "",
    lotSize: "",
    propertyType: "",
    buildingType: "",
    notes: "",
  });

  const seoConfig = pageSEO.evaluation();

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  async function handleSubmit(event) {
    event.preventDefault();

    const submissionData = {
      ...formData,
      access_key: constants.EMAIL_ACCESS_KEY,
      subject: "New Lead Interested in Home Evaluation",
    };

    try {
      const res = await fetch("https://api.web3forms.com/submit", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify(submissionData),
      });

      const data = await res.json();

      setPopup(
        data?.success
          ? {
            subject: "Thank You!",
            message:
              "Your evaluation request has been received. We will contact you shortly.",
          }
          : {
            subject: "Something went wrong",
            message: "Please try again later.",
          }
      );
    } catch {
      setPopup({
        subject: "Something went wrong",
        message: "Please try again later.",
      });
    }
  }

  const inputClass =
    "w-full rounded-2xl border border-cottage-border bg-cottage-secondary/30 px-5 py-4 text-sm outline-none transition focus:border-cottage-accent focus:bg-white";

  return (
    <>
      <SEO {...seoConfig} />

      <section className="relative overflow-hidden bg-cottage-secondary pt-40 pb-28">
        <motion.div
          variants={stagger}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="relative mx-auto max-w-6xl px-6 text-center"
        >
          <motion.p
            variants={fadeUp}
            className="uppercase tracking-[0.5em] font-semibold text-cottage-accent"
          >
            Complimentary Service
          </motion.p>

          <motion.h1
            variants={fadeUp}
            className="mt-6 font-serif text-5xl leading-none text-cottage-primary lg:text-7xl"
          >
            Discover Your
            <br />
            Home's True Value
          </motion.h1>

          <motion.p
            variants={fadeUp}
            className="mx-auto mt-8 max-w-3xl text-lg leading-8 text-cottage-muted"
          >
            Receive a personalized property evaluation based on current market
            trends, comparable homes, and expert local knowledge.
          </motion.p>
        </motion.div>
      </section>

      <section className="mx-auto max-w-[1400px] px-6 py-24">
        <div className="grid gap-12 lg:grid-cols-[480px_1fr]">

          <motion.div
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="hidden lg:block"
          >
            <div className="sticky top-32">
              <div className="overflow-hidden rounded-lg shadow-xl">
                <img
                  src={house}
                  alt="Home Evaluation"
                  className="h-[650px] w-full object-cover transition duration-700 hover:scale-105"
                />
              </div>

            </div>
          </motion.div>

          <motion.div
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="grid gap-8"
          >
            <div className="rounded-lg border border-cottage-border bg-white p-8 md:p-12">

              <h2 className="font-serif text-4xl text-cottage-primary">
                Request Your Evaluation
              </h2>

              <p className="mt-3 text-cottage-muted">
                Complete the form below and receive your complimentary home
                evaluation.
              </p>

              <form onSubmit={handleSubmit} className="mt-10 space-y-5">
                <input
                  required
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="Full Name"
                  className={inputClass}
                />

                <div className="grid gap-5 md:grid-cols-2">
                  <input
                    required
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="Email Address"
                    className={inputClass}
                  />

                  <input
                    required
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    placeholder="Phone Number"
                    className={inputClass}
                  />
                </div>

                <input
                  required
                  name="propertyAddress"
                  value={formData.propertyAddress}
                  onChange={handleInputChange}
                  placeholder="Property Address"
                  className={inputClass}
                />

                <div className="grid gap-5 md:grid-cols-2">
                  <input
                    required
                    name="city"
                    value={formData.city}
                    onChange={handleInputChange}
                    placeholder="City"
                    className={inputClass}
                  />

                  <input
                    required
                    name="provinceState"
                    value={formData.provinceState}
                    onChange={handleInputChange}
                    placeholder="Province / State"
                    className={inputClass}
                  />
                </div>

                <div className="grid gap-5 md:grid-cols-2">
                  <input
                    required
                    name="propertySize"
                    value={formData.propertySize}
                    onChange={handleInputChange}
                    placeholder="Property Size"
                    className={inputClass}
                  />

                  <input
                    required
                    name="lotSize"
                    value={formData.lotSize}
                    onChange={handleInputChange}
                    placeholder="Lot Size"
                    className={inputClass}
                  />
                </div>

                <select
                  required
                  name="propertyType"
                  value={formData.propertyType}
                  onChange={handleInputChange}
                  className={inputClass}
                >
                  <option value="">Property Type</option>
                  <option>Residential</option>
                  <option>Commercial</option>
                  <option>Land</option>
                  <option>Investment</option>
                </select>

                <select
                  required
                  name="buildingType"
                  value={formData.buildingType}
                  onChange={handleInputChange}
                  className={inputClass}
                >
                  <option value="">Building Type</option>
                  <option>House</option>
                  <option>Townhouse</option>
                  <option>Condo</option>
                  <option>Duplex</option>
                </select>

                <textarea
                  name="notes"
                  value={formData.notes}
                  onChange={handleInputChange}
                  placeholder="Additional Information"
                  className={`${inputClass} h-32 resize-none`}
                />

                <button className="btn-cottage mt-3">
                  Request Evaluation
                </button>
              </form>
            </div>
          </motion.div>
        </div>
      </section>

      {popup && (
        <>
          <div className="fixed inset-0 z-40 bg-black/40 backdrop-blur-sm" />

          <div className="fixed left-1/2 top-1/2 z-50 w-[90%] max-w-md -translate-x-1/2 -translate-y-1/2 rounded-3xl bg-white p-10 text-center shadow-2xl">

            <h3 className="font-heading text-3xl text-cottage-primary">
              {popup.subject}
            </h3>

            <p className="mt-4 leading-7 text-cottage-muted">
              {popup.message}
            </p>

            <button
              onClick={() => setPopup(false)}
              className="btn-cottage mt-8"
            >
              Close
            </button>

          </div>
        </>
      )}

    </>
  );
}